import { getOperationType } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/bulk-action-utils';
import { BULK_EDIT_TRANSACTIONS_FORM_FIELD } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/BulkUpdateTransactionsDrawer/useBulkUpdateTransactionsDrawerController';
import { TransactionBulkOperationParams } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/types.ts';
import {
	getFinalSelectionCount,
	getTableAllSelected,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { getTableMeta } from '@dealstack/frontend/shared/components/Table/MantineTable/types';
import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { GetTransactionRes, TransactionFilterDto } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Table } from '@tanstack/react-table';
import { MutableRefObject } from 'react';
import {
	BulkAction,
	BulkActionGroup,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ActionsButton/ActionsButton.types';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export const createBulkActions = (
	table: Table<GetTransactionRes>,
	{
		showDeletedTable,
		isLoading,
		hasEditorPermission,
		hasAdminPermission,
		userIsAdmin,
		selectedEntityId,
		dealId,
		tCapManage,
		initiateBulkRestore,
		downloadExcel,
		initiateBulkUpdate,
		initiateBulkDelete,
		setForceDelete,
		returnToTable,
		showDeleteTable,
		dataOperations,
		dataOperationsRef,
	}: {
		showDeletedTable: boolean;
		isLoading: boolean;
		hasEditorPermission: boolean;
		hasAdminPermission: boolean;
		userIsAdmin: boolean;
		selectedEntityId: string;
		dealId: string;
		tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>;
		initiateBulkRestore: (payload: TransactionBulkOperationParams) => void;
		downloadExcel: (params: TransactionBulkOperationParams) => Promise<void>;
		initiateBulkUpdate: (
			payload: TransactionBulkOperationParams,
			updateField: BULK_EDIT_TRANSACTIONS_FORM_FIELD
		) => void;
		initiateBulkDelete: (payload: TransactionBulkOperationParams) => void;
		setForceDelete: (value: boolean) => void;
		returnToTable: () => void;
		showDeleteTable: () => void;
		dataOperations: TableOperations<TransactionFilterDto>;
		dataOperationsRef?: MutableRefObject<TableOperations<TransactionFilterDto>>;
	}
): BulkActionGroup<GetTransactionRes> => {
	const primary: BulkAction<GetTransactionRes>[] = [];
	const secondary: BulkAction<GetTransactionRes>[] = [];
	const special: BulkAction<GetTransactionRes>[] = [];

	if (showDeletedTable) {
		primary.push({
			key: 'restore_all',
			label: tCapManage(
				`table.bulk_actions.${getTableAllSelected(table) ? 'restore_filtered' : 'restore_selection'}`
			),
			buttonLabel: () =>
				getTableAllSelected(table)
					? `${tCapManage('table.bulk_actions.restore_filtered')} (${getFinalSelectionCount(table)})`
					: tCapManage('table.bulk_actions.restore_selection'),
			disabled: isLoading,
			onExecute: () =>
				initiateBulkRestore({
					operationType: getOperationType(table),
					selectedEntityId,
					table,
					dealId,
				}),
		});
	} else {
		primary.push({
			key: 'download',
			label: tCapManage('table.bulk_actions.download_selection'),
			buttonLabel: () => {
				const { manualPagination, greedySelecting } = getTableMeta(table);
				const isGreedySelection = greedySelecting && manualPagination;

				return getTableAllSelected(table) || isGreedySelection
					? `${tCapManage('table.bulk_actions.download_filtered')} (${getFinalSelectionCount(table)})`
					: tCapManage('table.bulk_actions.download_selection');
			},
			disabled: isLoading,
			dataTestId: TEST_ELEMENT_IDS.CAP_TRANSACTION_DOWNLOAD_SELECTED,
			onExecute: () =>
				downloadExcel({
					operationType: getOperationType(table),
					selectedEntityId,
					table,
					dealId,
					dataOperations: dataOperationsRef?.current ?? dataOperations,
				}),
		});

		if (hasEditorPermission) {
			primary.push(
				{
					key: 'updateDate',
					label: tCapManage('table.bulk_actions.update_date'),
					disabled: isLoading,
					onExecute: () =>
						initiateBulkUpdate(
							{
								operationType: getOperationType(table),
								selectedEntityId,
								table,
								dealId,
								dataOperations,
							},
							BULK_EDIT_TRANSACTIONS_FORM_FIELD.DATE
						),
				},
				{
					key: 'update_type',
					label: tCapManage('table.bulk_actions.update_type'),
					disabled: isLoading,
					dataTestId: TEST_ELEMENT_IDS.CAP_TRANSACTION_BULK_UPDATE_TYPE,
					onExecute: () =>
						initiateBulkUpdate(
							{
								operationType: getOperationType(table),
								selectedEntityId,
								table,
								dealId,
								dataOperations,
							},
							BULK_EDIT_TRANSACTIONS_FORM_FIELD.TYPE
						),
				},
				{
					key: 'update_buyer',
					label: tCapManage('table.bulk_actions.update_buyer'),
					disabled: isLoading,
					dataTestId: TEST_ELEMENT_IDS.CAP_TRANSACTION_BULK_UPDATE_BUYER,
					onExecute: () =>
						initiateBulkUpdate(
							{
								operationType: getOperationType(table),
								selectedEntityId,
								table,
								dealId,
								dataOperations,
							},
							BULK_EDIT_TRANSACTIONS_FORM_FIELD.BUYER
						),
				},
				{
					key: 'update_seller',
					label: tCapManage('table.bulk_actions.update_seller'),
					disabled: isLoading,
					dataTestId: TEST_ELEMENT_IDS.CAP_TRANSACTION_BULK_UPDATE_SELLER,
					onExecute: () =>
						initiateBulkUpdate(
							{
								operationType: getOperationType(table),
								selectedEntityId,
								table,
								dealId,
								dataOperations,
							},
							BULK_EDIT_TRANSACTIONS_FORM_FIELD.SELLER
						),
				},
				{
					key: 'update_buyer_mip',
					label: tCapManage('table.bulk_actions.update_buyer_holding'),
					disabled: isLoading,
					dataTestId: TEST_ELEMENT_IDS.CAP_TRANSACTION_BULK_UPDATE_BUYER_MIP,
					onExecute: () =>
						initiateBulkUpdate(
							{
								operationType: getOperationType(table),
								selectedEntityId,
								table,
								dealId,
								dataOperations,
							},
							BULK_EDIT_TRANSACTIONS_FORM_FIELD.BUYER_MIP
						),
				},
				{
					key: 'update_seller_mip',
					label: tCapManage('table.bulk_actions.update_seller_holding'),
					disabled: isLoading,
					onExecute: () =>
						initiateBulkUpdate(
							{
								operationType: getOperationType(table),
								selectedEntityId,
								table,
								dealId,
								dataOperations,
							},
							BULK_EDIT_TRANSACTIONS_FORM_FIELD.SELLER_MIP
						),
				}
			);
		}
	}

	if (!showDeletedTable && hasEditorPermission) {
		secondary.push({
			key: 'delete',
			label: tCapManage(
				`table.bulk_actions.${getTableAllSelected(table) ? 'delete_filtered' : 'delete_selection'}`
			),
			color: 'red',
			disabled: isLoading,
			onExecute: () => {
				setForceDelete(false);
				initiateBulkDelete({
					operationType: getOperationType(table),
					selectedEntityId,
					table,
					dealId,
					dataOperations,
				});
			},
		});

		if (userIsAdmin) {
			secondary.push({
				key: 'force-delete',
				label: 'Force Delete',
				color: 'red',
				disabled: isLoading,
				onExecute: () => {
					setForceDelete(true);
					initiateBulkDelete({
						operationType: getOperationType(table),
						selectedEntityId,
						table,
						dealId,
						dataOperations,
					});
				},
			});
		}
	}

	if (hasAdminPermission) {
		if (showDeletedTable) {
			special.push({
				key: 'return_to_table',
				label: tCapManage('table.bulk_actions.return_to_table'),
				disabled: isLoading,
				executeImmediately: true,
				onExecute: () => {
					getTableMeta(table).resetActionState?.();
					returnToTable();
				},
			});
		} else {
			special.push({
				key: 'show_deleted',
				label: tCapManage('table.bulk_actions.show_deleted'),
				disabled: isLoading,
				executeImmediately: true,
				onExecute: showDeleteTable,
			});
		}
	}

	return { primary, secondary, special };
};
